import React, { useState, useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import MonitoringCard from './components/MonitoringCard';
import MonitoringSection from './components/MonitoringSection';
import MonitoringLegend from './components/MonitoringLegend';
import { useStore } from '../../stores/store';
import { OverviewDto, KeyValue } from '../../models/Pharse2/Overview';
import { ProjectDistinct, LocationWithSourceTypeDto, SourceTypeInfoDto } from '../../models/Pharse2/LocationMap';
import {
  monitoringConfig,
  getResourceTypeIds,
  getSourceTypeNames,
  isSourceTypeForMonitoring,
  getUpdateInterval,
  sortMonitoringDataByLocation
} from './config/monitoringConfig';
import LoadingComponent from '../../components/LoadingComponent';

import { ResourceDataDto } from '../../models/Pharse2/ResourceData';
import ReportDialogButton from './components/ReportDialogButton';

export interface MonitoringData {
  id: string;
  value: number;
  unit: string;
  location: string;
  locationId: number;
  timestamp?: string;
  chartData?: ResourceDataDto[]; // History data for chart (only for vibration)
  alertThreshold?: number | null; // Alert threshold from LocationGroup
  alarmThreshold?: number | null; // Alarm threshold from LocationGroup
  projectName?: string | null; // Project name for special threshold logic
}

export interface MonitoringSectionData {
  title: string;
  data: MonitoringData[];
}

const MonitoringPage: React.FC = observer(() => {
  const { overviewStore, userStore, locationMapStore, resourceDataStore } = useStore();
  const { loadingInitial, getOverviewDtoList } = overviewStore;
  const { getResourceData } = resourceDataStore;
  const { user, CheckIfCastcoAdmin } = userStore;
  const navigate = useNavigate();

  // 檢查是否為管理員
  useEffect(() => {
    if (user && !CheckIfCastcoAdmin() && user.role !== 'Hospital_User') {
      // 如果不是管理員，重定向到 dashboard
      navigate('/dashboard');
    }
  }, [user, CheckIfCastcoAdmin, navigate]);

  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [projectList, setProjectList] = useState<ProjectDistinct[]>([]);
  const [locationList, setLocationList] = useState<LocationWithSourceTypeDto[]>([]);

  const [noiseData, setNoiseData] = useState<MonitoringData[]>([]);
  const [tspData, setTspData] = useState<MonitoringData[]>([]);
  const [vibrationData, setVibrationData] = useState<MonitoringData[]>([]);

  // 使用 useRef 來保持 alertThreshold Map 的引用，避免每次渲染時重新創建
  const locationAlertThresholdMapRef = useRef<Map<number, number | null | undefined>>(new Map());
  // 使用 useRef 來保持 alertThreshold Map 的引用，避免每次渲染時重新創建
  const locationAlarmThresholdMapRef = useRef<Map<number, number | null | undefined>>(new Map());

  // 獲取項目列表
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const projects = await locationMapStore.loadProjectDistinctList();
        if (projects) {
          console.log("projects", projects);
          setProjectList(projects);
        }
      } catch (error) {
        console.error('Failed to fetch project list:', error);
      }
    };

    fetchProjects();
  }, [locationMapStore]);

  const convertOverviewDtoToLocationWithSourceType = (overviewDto: OverviewDto): LocationWithSourceTypeDto => {
    // 保存 alertThreshold 到 map 中
    locationAlertThresholdMapRef.current.set(overviewDto.locationId, overviewDto.alertThreshold);
    locationAlarmThresholdMapRef.current.set(overviewDto.locationId, overviewDto.alarmThreshold);
    // 將 sourceTypeValues (KeyValue[]) 轉換為 sourceTypes (SourceTypeInfoDto[])
    const sourceTypes: SourceTypeInfoDto[] = overviewDto.sourceTypeValues.map((kv: KeyValue) => ({
      id: kv.resourceTypeId,
      sourceType: kv.resourceTypeName,
      unit: kv.unit || undefined,
      status: true, // OverviewDto 中的 sourceType 都是有效的
      access: true, // 默認值，如果需要可以從其他地方獲取
      remark: undefined
    }));

    // 直接使用 OverviewDto 中的 projectName，這樣在選擇 "All Projects" 時也能正確獲取項目名稱
    const projectName = overviewDto.projectName || null;

    return {
      locationId: overviewDto.locationId,
      projectId: overviewDto.projectId,
      name: overviewDto.locationName,
      loggersetName: undefined, // OverviewDto 中沒有 loggersetName，可以從其他地方獲取或留空
      latitude: undefined, // OverviewDto 中沒有坐標信息
      longtitude: undefined,
      batteryValue: 0,
      locationAlert: overviewDto.alarmValue,
      sourceTypes: sourceTypes,
      projectName: projectName || undefined
    };
  };

  // 獲取位置列表（根據選中的項目，使用 getOverviewDtoList API）
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        // 清空 alertThreshold Map，準備填充新數據
        locationAlertThresholdMapRef.current.clear();

        // 使用 getOverviewDtoList API 獲取數據
        const overviewDtos = await getOverviewDtoList(
          selectedProjectId !== null ? selectedProjectId : undefined
        );

        if (overviewDtos) {
          // console.log("Overview DTOs:", overviewDtos);
          // 轉換為 LocationWithSourceTypeDto 格式
          const convertedLocations = overviewDtos.map(convertOverviewDtoToLocationWithSourceType);
          setLocationList(convertedLocations);
        } else {
          setLocationList([]);
        }
      } catch (error) {
        console.error('Failed to fetch location list from overview:', error);
        setLocationList([]);
      }
    };

    fetchLocations();
  }, [selectedProjectId, getOverviewDtoList]);

  // 獲取 Noise Monitoring 數據（根據 sourceType 名稱過濾）
  useEffect(() => {
    if (locationList.length === 0) {
      setNoiseData([]);
      return;
    }

    const fetchNoiseData = async () => {
      try {
        const noiseSourceTypeNames = getSourceTypeNames('noise');
        const noiseDataPromises: Promise<MonitoringData | null>[] = [];

        // 為每個位置查找屬於 Noise Monitoring 的 sourceTypes
        for (const location of locationList) {
          // 過濾出屬於 Noise Monitoring 的 sourceTypes
          const noiseSourceTypes = location.sourceTypes.filter(st =>
            noiseSourceTypeNames.some(name => st.sourceType.toLowerCase() === name.toLowerCase())
          );

          // 為每個匹配的 sourceType 獲取最新數據
          for (const sourceType of noiseSourceTypes) {
            noiseDataPromises.push(
              getResourceData(location.locationId, sourceType.id)
                .then(resourceDataList => {
                  if (resourceDataList && resourceDataList.length > 0) {
                    const latestData = resourceDataList[0];
                    if (latestData && latestData.value !== null && latestData.resourceTypeId !== null) {
                      return {
                        id: `${location.locationId}-${sourceType.id}`,
                        value: latestData.value,
                        //unit: sourceType.unit || 'dB', // 從 SourceType 獲取實際單位
                        unit: sourceType.unit || 'dB(A)', // 從 SourceType 獲取實際單位
                        location: location.name,
                        locationId: location.locationId,
                        timestamp: latestData.dateTime || undefined,
                        alertThreshold: locationAlertThresholdMapRef.current.get(location.locationId),
                        alarmThreshold: locationAlarmThresholdMapRef.current.get(location.locationId),
                        projectName: location.projectName || null
                      };
                    }
                  }
                  return null;
                })
                .catch(error => {
                  console.error(`Failed to fetch noise data for location ${location.locationId}, sourceType ${sourceType.sourceType}:`, error);
                  return null;
                })
            );
          }
        }

        const results = await Promise.all(noiseDataPromises);
        const validData = results.filter((item): item is MonitoringData => item !== null);
        // 根據配置的排序順序排序
        const sortedData = sortMonitoringDataByLocation(validData);
        setNoiseData(sortedData);
      } catch (error) {
        console.error('Failed to fetch noise data:', error);
      }
    };

    fetchNoiseData();
    const interval = setInterval(fetchNoiseData, getUpdateInterval('noise'));

    return () => clearInterval(interval);
  }, [locationList, getResourceData]);

  // 獲取 TSP Monitoring 數據（根據 sourceType 名稱過濾）
  useEffect(() => {
    if (locationList.length === 0) {
      setTspData([]);
      return;
    }

    const fetchTspData = async () => {
      try {
        const tspSourceTypeNames = getSourceTypeNames('tsp');
        const tspDataPromises: Promise<MonitoringData | null>[] = [];

        // 為每個位置查找屬於 TSP Monitoring 的 sourceTypes
        for (const location of locationList) {
          // 過濾出屬於 TSP Monitoring 的 sourceTypes (ppm, pvs)
          const tspSourceTypes = location.sourceTypes.filter(st =>
            tspSourceTypeNames.some(name => st.sourceType.toLowerCase() === name.toLowerCase())
          );

          // 為每個匹配的 sourceType 獲取最新數據
          for (const sourceType of tspSourceTypes) {
            tspDataPromises.push(
              getResourceData(location.locationId, sourceType.id)
                .then(resourceDataList => {
                  if (resourceDataList && resourceDataList.length > 0) {
                    const latestData = resourceDataList[0];
                    if (latestData && latestData.value !== null && latestData.resourceTypeId !== null) {
                      return {
                        id: `${location.locationId}-${sourceType.id}`,
                        value: latestData.value,
                        unit: sourceType.unit || 'ug/m³', // 從 SourceType 獲取實際單位
                        location: location.name,
                        locationId: location.locationId,
                        timestamp: latestData.dateTime || undefined,
                        alertThreshold: locationAlertThresholdMapRef.current.get(location.locationId),
                        alarmThreshold: locationAlarmThresholdMapRef.current.get(location.locationId),
                        projectName: location.projectName || null
                      };
                    }
                  }
                  return null;
                })
                .catch(error => {
                  console.error(`Failed to fetch TSP data for location ${location.locationId}, sourceType ${sourceType.sourceType}:`, error);
                  return null;
                })
            );
          }
        }

        const results = await Promise.all(tspDataPromises);
        const validData = results.filter((item): item is MonitoringData => item !== null);
        // 根據配置的排序順序排序
        const sortedData = sortMonitoringDataByLocation(validData);
        setTspData(sortedData);
      } catch (error) {
        console.error('Failed to fetch TSP data:', error);
      }
    };

    fetchTspData();
    const interval = setInterval(fetchTspData, getUpdateInterval('tsp'));

    return () => clearInterval(interval);
  }, [locationList, getResourceData]);

  // 獲取 Vibration Monitoring 數據（根據 sourceType 名稱過濾）
  useEffect(() => {
    if (locationList.length === 0) {
      setVibrationData([]);
      return;
    }

    const fetchVibrationData = async () => {
      try {
        const vibrationSourceTypeNames = getSourceTypeNames('vibration');
        const vibrationDataPromises: Promise<MonitoringData | null>[] = [];

        // 如果沒有配置 vibration sourceType 名稱，則跳過
        if (vibrationSourceTypeNames.length === 0) {
          setVibrationData([]);
          return;
        }

        // 為每個位置查找屬於 Vibration Monitoring 的 sourceTypes
        for (const location of locationList) {
          // 過濾出屬於 Vibration Monitoring 的 sourceTypes
          const vibrationSourceTypes = location.sourceTypes.filter(st =>
            vibrationSourceTypeNames.some(name => st.sourceType.toLowerCase() === name.toLowerCase())
          );

          // 為每個匹配的 sourceType 獲取最新數據和歷史數據（用於圖表）
          for (const sourceType of vibrationSourceTypes) {
            vibrationDataPromises.push(
              Promise.all([
                getResourceData(location.locationId, sourceType.id), // Latest data
                getResourceData(location.locationId, sourceType.id, true) // History data (latest 12 records)
              ])
                .then(([latestDataList, historyDataList]) => {
                  if (latestDataList && latestDataList.length > 0) {
                    const latestData = latestDataList[0];
                    if (latestData && latestData.value !== null && latestData.resourceTypeId !== null) {
                      return {
                        id: `${location.locationId}-${sourceType.id}`,
                        value: latestData.value,
                        unit: sourceType.unit || 'um/s', // 從 SourceType 獲取實際單位
                        location: location.name,
                        locationId: location.locationId,
                        timestamp: latestData.dateTime || undefined,
                        chartData: historyDataList && historyDataList.length > 0 ? historyDataList : undefined,
                        alertThreshold: locationAlertThresholdMapRef.current.get(location.locationId),
                        alarmThreshold: locationAlarmThresholdMapRef.current.get(location.locationId),
                        projectName: location.projectName || null
                      };
                    }
                  }
                  return null;
                })
                .catch(error => {
                  console.error(`Failed to fetch vibration data for location ${location.locationId}, sourceType ${sourceType.sourceType}:`, error);
                  return null;
                })
            );
          }
        }


        const results = await Promise.all(vibrationDataPromises);
        const validData = results.filter((item): item is MonitoringData => item !== null);
        // 根據配置的排序順序排序
        const sortedData = sortMonitoringDataByLocation(validData);
        setVibrationData(sortedData);
      } catch (error) {
        console.error('Failed to fetch vibration data:', error);
      }
    };

    fetchVibrationData();
    const interval = setInterval(fetchVibrationData, getUpdateInterval('vibration'));

    return () => clearInterval(interval);
  }, [locationList, getResourceData]);


  // 如果用戶未加載或不是管理員，顯示加載或重定向
  if (!user) {
    return <LoadingComponent />;
  }

  if (!CheckIfCastcoAdmin() && user.role !== 'Hospital_User') {
    return (
      <div className="pageParentDiv bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Access Denied</h2>
          <p className="text-gray-600">You do not have permission to access this page.</p>
          <p className="text-gray-600 mt-2">Only administrators can view the monitoring page.</p>
        </div>
      </div>
    );
  }

  if (loadingInitial && locationList.length === 0) {
    return <LoadingComponent />;
  }

  const parseProjectName = (projectName: string) => {
    // 處理特定醫院名稱
    if (projectName.startsWith('聖母醫院')) {
      return 'Our Lady of Maryknoll Hospital';
    }
    if (projectName.startsWith('葛量洪醫院')) {
      return 'Grantham Hospital';
    }
    // 處理所有以「XXX醫院」開頭的項目名稱（通用處理）
    // 匹配模式：以「醫院」結尾，且前面有中文字符
    const hospitalPattern = /^(.+?)醫院/;
    const match = projectName.match(hospitalPattern);
    if (match) {
      // 如果匹配到「XXX醫院」模式，返回原始名稱（或可以根據需要進行其他處理）
      // 這裡保持原始名稱，因為只有特定醫院需要轉換
      return projectName;
    }
    return projectName;
  }

  // 獲取最常見的項目名稱（當選擇 All Projects 時）
  const getMostCommonProjectName = (): string => {
    if (locationList.length === 0) return '';

    // 統計每個項目出現的次數
    const projectCountMap = new Map<number, number>();
    locationList.forEach(location => {
      if (location.projectId) {
        const count = projectCountMap.get(location.projectId) || 0;
        projectCountMap.set(location.projectId, count + 1);
      }
    });

    // 找到出現次數最多的項目 ID
    let maxCount = 0;
    let mostCommonProjectId: number | null = null;
    projectCountMap.forEach((count, projectId) => {
      if (count > maxCount) {
        maxCount = count;
        mostCommonProjectId = projectId;
      }
    });

    // 根據項目 ID 找到項目名稱，並應用 parseProjectName 處理
    if (mostCommonProjectId) {
      const project = projectList.find(p => p.id === mostCommonProjectId);
      if (project) {
        // 應用 parseProjectName 邏輯處理項目名稱
        return parseProjectName(project.name);
      }
    }

    return '';
  }


  return (
    <div className="monitoringPageParentDiv">
      {/* Header with Project Selector */}
      <div className="mb-0 rgb(48, 59, 75) rounded-lg p-2 text-white flex-shrink-0">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4 flex-wrap">
            <h1 className="text-2xl font-bold">
              Real-time Monitoring{selectedProjectId ? (() => {
                const selectedProject = projectList.find(p => p.id === selectedProjectId);
                if (!selectedProject) return '';
                return ` for ${parseProjectName(selectedProject.name)}`;
              })() : (() => {
                const mostCommonProject = getMostCommonProjectName();
                return mostCommonProject ? ` for ${mostCommonProject}` : '';
              })()}
            </h1>
            {/* Legend */}
            <MonitoringLegend />
          </div>
          <div className="flex items-center gap-3">
            {
              CheckIfCastcoAdmin() && (
                <ReportDialogButton />
              )
            }
            <label htmlFor="project-select" className="text-sm font-medium text-gray-300 whitespace-nowrap">
              Select Project:
            </label>
            <select
              id="project-select"
              value={selectedProjectId || ''}
              onChange={(e) => {
                const value = e.target.value;
                setSelectedProjectId(value === '' ? null : parseInt(value));
              }}
              className="px-4 py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[200px]"
            >
              <option value="">All Projects</option>
              {projectList.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Main Content Area - 使用 flex-1 佔滿剩餘空間 */}
      {/* 如果 Noise 和 TSP 都為空，讓 Vibration 佔據全寬 */}
      {noiseData.length === 0 && tspData.length === 0 ? (
        <div className="flex-1 min-h-0">
          <div className="rounded-lg p-2 text-white flex flex-col min-h-0 h-full">
            <div className="flex-1 flex flex-col min-h-0">
              <MonitoringSection
                title="Vibration Monitoring"
                data={vibrationData}
                monitoringType="vibration"
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 flex-1 min-h-0">
          {/* 左側 1/3：Noise 和 TSP Monitoring */}
          <div className="lg:col-span-1 rounded-lg p-2 text-white flex flex-col min-h-0">
            {/* Noise Monitoring Section */}
            <div className={tspData.length === 0 ? "flex-1 flex flex-col min-h-0" : "flex-1 flex flex-col min-h-0"}>
              <MonitoringSection
                title="Noise Monitoring"
                data={noiseData}
                monitoringType="noise"
              />
            </div>

            {/* TSP Monitoring Section - 只在有數據時顯示 */}
            {tspData.length > 0 && (
              <div className="flex-1 flex flex-col min-h-0">
                <MonitoringSection
                  title="TSP Monitoring"
                  data={tspData}
                  monitoringType="tsp"
                />
              </div>
            )}
          </div>

          {/* 右側 2/3：Vibration Monitoring */}
          <div className="lg:col-span-2 rounded-lg p-2 text-white flex flex-col min-h-0">
            <div className="flex-1 flex flex-col min-h-0">
              <MonitoringSection
                title="Vibration Monitoring"
                data={vibrationData}
                monitoringType="vibration"
                limit2xlColumns={noiseData.length > 0 || tspData.length > 0}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default MonitoringPage;

